import { IconButton } from "@chakra-ui/button";
import { Box, Flex, Text } from "@chakra-ui/react";
import currency from "currency.js";
import { getIn, useFormikContext } from "formik";
import React from "react";
import { FormattedDate } from "react-intl";
import { Link } from "react-router-dom";

import { buildInvoicePagePath, RouteParams } from "../config/routes";
import { useSupplier } from "../context/SupplierContext";
import { useSingleParam } from "../hooks/useSingleParam";
import { SelectField } from "./form/Select";
import { Link as LinkIcon, TrashIcon } from "./icons";
import { CheckboxCell, EditableTextCell, Table } from "./Table";

interface CollectionNoteLine {
  id: string | null;
  quantity: string;
  description: string;
  value: string;
  vat: string;
  total: string;
  invoiced: boolean;
  invoiceId?: string;
  archivedEwcCode?: EWCCode | null;
  canChangeEwcCode?: boolean;
  invoicedEwcCode?: EWCCode | null;
  ewcCodeChangedAt?: string | null;
}

interface EWCCode {
  id: string;
  code: string;
  product: string;
  note?: string | null;
  isArchived?: boolean;
}

interface EWCCodeOption {
  id: string;
  label: string;
  note: string | null;
}

// Native <option>s can't wrap, so long notes are cut short in the label. The full note is
// available as a tooltip on the select.
const MAX_LABEL_NOTE_LENGTH = 50;

const buildEwcCodeLabel = (code: EWCCode) => {
  const label = `${code.code}: ${code.product}`;
  const note = code.note?.trim();

  if (!note) {
    return label;
  }

  const shortNote =
    note.length > MAX_LABEL_NOTE_LENGTH
      ? `${note.slice(0, MAX_LABEL_NOTE_LENGTH).trimEnd()}…`
      : note;

  return `${label} – ${shortNote}`;
};

const buildEwcCodeOption = (code: EWCCode): EWCCodeOption => {
  return { id: code.id, label: buildEwcCodeLabel(code), note: code.note?.trim() || null };
};

/**
 * Active codes are always offered. A line's existing archived code is only offered while it is
 * still the selected value, so it keeps displaying but can't be re-selected once changed. The
 * code an invoiced line was invoiced with is always offered, so a correction can be undone.
 */
export const buildEwcCodeOptions = (
  activeCodes: EWCCode[],
  currentValue: string | null | undefined,
  archivedEwcCode: EWCCode | null | undefined,
  invoicedEwcCode?: EWCCode | null
): EWCCodeOption[] => {
  const options = activeCodes.map(buildEwcCodeOption);
  const isOffered = (code: EWCCode) => options.some((option) => option.id === code.id);

  if (invoicedEwcCode && !isOffered(invoicedEwcCode)) {
    const invoicedOption = buildEwcCodeOption(invoicedEwcCode);

    options.push({ ...invoicedOption, label: `${invoicedOption.label} (archived, invoiced code)` });
  }

  if (archivedEwcCode && currentValue === archivedEwcCode.id && !isOffered(archivedEwcCode)) {
    const archivedOption = buildEwcCodeOption(archivedEwcCode);

    options.push({ ...archivedOption, label: `${archivedOption.label} (archived)` });
  }

  return options;
};

interface NominalCode {
  id: string;
  code: string;
}

interface CollectionNotesTableFieldProps {
  name: string;
  data: CollectionNoteLine[];
  ewcCodes: EWCCode[];
  nominalCodes: NominalCode[];
  onRemoveRow: (index: number) => void;
}

export const CollectionNotesTableField: React.FC<CollectionNotesTableFieldProps> = (props) => {
  const supplierSlug = useSingleParam(RouteParams.SupplierSlug);

  const instanceProps = React.useMemo(() => {
    return {
      tableFieldName: props.name,
      ewcCodes: props.ewcCodes,
      nominalCodes: props.nominalCodes,
    };
  }, [props.name, props.ewcCodes, props.nominalCodes]);

  return (
    <Box
      sx={{
        "table tbody input,select": {
          fontSize: "0.8rem !important",
          pl: 2,
          pr: 2,
        },
      }}
    >
      <Table
        mode="compact"
        data-testid="collection-note-lines"
        data={props.data}
        columns={collectionNoteLinesTableColumns}
        instanceProps={instanceProps}
        rowActions={({ index, row }) => {
          return (
            <Flex alignItems="center">
              {row.invoiced ? null : (
                <IconButton
                  aria-label="Delete collection note line"
                  icon={<TrashIcon color="red.700" />}
                  onClick={() => {
                    props.onRemoveRow(index);
                  }}
                />
              )}

              {row.invoiceId ? (
                <Box ml={2}>
                  <Link to={buildInvoicePagePath(supplierSlug, row.invoiceId)}>
                    <LinkIcon />
                  </Link>
                </Box>
              ) : null}
            </Flex>
          );
        }}
      />
    </Box>
  );
};

interface CellProps {
  tableFieldName: string;
  ewcCodes: EWCCode[];
  nominalCodes: NominalCode[];
  row: { index: number; original: CollectionNoteLine };
  column: {
    id: string;
    placeholder: string;
  };
}

// Invoiced lines are locked; the API rejects any change to them other than an EWC code correction.
const LockableTextCell = (props: CellProps) => {
  return <EditableTextCell {...props} isReadOnly={props.row.original.invoiced} />;
};

const collectionNoteLinesTableColumns = [
  {
    id: "quantity",
    accessor: "quantity",
    Header: "Quantity",
    placeholder: "Quantity",
    Cell: LockableTextCell,
    width: "10%",
  },
  {
    id: "description",
    accessor: "description",
    Header: "Description",
    placeholder: "Description",
    Cell: LockableTextCell,
    width: "30%",
  },
  {
    id: "ewcCodeId",
    accessor: "ewcCodeId",
    Header: "EWC Code",
    width: "10%",
    Cell: (props: CellProps) => {
      const { values } = useFormikContext<any>();
      const rowKey = `${props.tableFieldName}[${props.row.index}]`;
      const fieldKey = `${rowKey}.${props.column.id}`;

      const { invoiced, canChangeEwcCode, invoicedEwcCode, ewcCodeChangedAt } = props.row.original;

      const currentValue = getIn(values, fieldKey);
      const options = buildEwcCodeOptions(
        props.ewcCodes,
        currentValue,
        getIn(values, `${rowKey}.archivedEwcCode`),
        invoicedEwcCode
      );
      const selectedNote = options.find((option) => option.id === currentValue)?.note;

      return (
        <Box>
          <SelectField
            name={fieldKey}
            selectProps={{
              title: selectedNote ?? undefined,
              isDisabled: invoiced && !canChangeEwcCode,
            }}
          >
            {/* An invoiced line's code can be corrected but not cleared */}
            {invoiced && currentValue ? null : <option value=""></option>}
            {options.map((option) => {
              return (
                <option key={option.id} value={option.id} title={option.note ?? undefined}>
                  {option.label}
                </option>
              );
            })}
          </SelectField>

          {invoicedEwcCode && currentValue !== invoicedEwcCode.id ? (
            <Text fontSize="xs" color="orange.700" mt={1}>
              Invoiced as {invoicedEwcCode.code}
              {invoicedEwcCode.isArchived ? " (archived)" : ""}
              {ewcCodeChangedAt ? (
                <React.Fragment>
                  {" · updated "}
                  <FormattedDate
                    value={ewcCodeChangedAt}
                    year="numeric"
                    month="short"
                    day="2-digit"
                  />
                </React.Fragment>
              ) : null}
            </Text>
          ) : null}
        </Box>
      );
    },
  },
  {
    id: "nominalCodeId",
    accessor: "nominalCodeId",
    Header: "Nominal Code",
    width: "10%",
    Cell: (props: CellProps) => {
      const fieldKey = `${props.tableFieldName}[${props.row.index}].${props.column.id}`;

      return (
        <SelectField name={fieldKey} selectProps={{ isDisabled: props.row.original.invoiced }}>
          <option value=""></option>
          {props.nominalCodes.map((code) => {
            return (
              <option key={code.id} value={code.id}>
                {code.code}
              </option>
            );
          })}
        </SelectField>
      );
    },
  },
  {
    id: "value",
    accessor: "value",
    Header: "Value",
    placeholder: "20.00",
    Cell: (props: CellProps) => {
      const { supplier } = useSupplier();

      const { setFieldValue, handleChange } = useFormikContext<any>();

      return (
        <EditableTextCell
          {...props}
          isReadOnly={props.row.original.invoiced}
          onChange={(e: React.ChangeEvent<any>) => {
            handleChange(e);

            const inputValue = e.currentTarget.value;

            if (!inputValue || inputValue.trim() === "") {
              return;
            }

            const vat = currency(inputValue).multiply(supplier.vat / 100);
            const total = currency(inputValue).add(vat);

            const fieldRowKey = `${props.tableFieldName}[${props.row.index}]`;

            setFieldValue(`${fieldRowKey}.vat`, vat.value.toString());
            setFieldValue(`${fieldRowKey}.total`, total.value.toString());
          }}
        />
      );
    },
  },
  {
    id: "vat",
    accessor: "vat",
    Header: "VAT",
    Cell: LockableTextCell,
    placeholder: "4.00",
  },
  {
    id: "total",
    accessor: "total",
    Header: "Total",
    Cell: LockableTextCell,
    placeholder: "24.00",
  },
  {
    id: "invoiced",
    accessor: "invoiced",
    Header: "Invoiced",
    Cell: CheckboxCell,
    readonly: true,
    width: "7.5%",
  },
];
